<template>
  <draggable
    ref="drag"
    v-model="files"
    v-bind="dragOptions"
    multi-drag
    tag="tbody"
    selected-class="multi-drag"
    @update="onUpdate"
    @add="onAdd"
    @remove="onRemove"
    @start="onDragStart"
    @end="onDragEnd"
  >
    <template v-if="files.length > 0">
      <tr v-for="item in files" :key="item.id">
        <td class="d-flex align-center">
          <div class="handle-drag" style="cursor: move">
            <v-icon>mdi-drag</v-icon>
          </div>
          <v-checkbox
            hide-details
            class="pa-0 ma-0"
            v-model="selectedDeliveries[item.id]"
            @change="onCheckboxChange"
          ></v-checkbox>
        </td>
        <td>
          {{ item?.cargoNumber }}
        </td>
        <td v-if="hasDirectDelivery">
          {{ formatPrice(item?.totalTranshipmentPrice) }}
        </td>

        <td>{{ formatPrice(item?.totalPriceNfes) }}</td>
        <td>{{ formatWeight(item?.weightDeliveries) }}kg</td>
        <td>
          {{ item.isDirectDelivery ? 'Entrega direta' : 'Transferência' }}
        </td>
        <td v-if="hasDirectDelivery">
          <TruncateDataWithTooltip
            v-if="item.transhipmentName != null"
            :data="item.transhipmentName"
            :lengthDataToTruncate="truncateLimit"
          ></TruncateDataWithTooltip>
        </td>
      </tr>
    </template>
    <template v-else>
      <tr>
        <td colspan="10" class="text-center">Nenhuma carga alocado para esse embarque</td>
      </tr>
    </template>
  </draggable>
</template>

<script>
import TruncateDataWithTooltip from '@/components/Table/TruncateDataWithTooltip.vue';
import copyToClipboard from '@/utils/copyToClipboard';
import draggable from 'vuedraggable';
import maskPrice from '@/utils/maskPrice';
import maskWeight from '@/utils/maskWeight';
import { mapActions } from 'vuex';

export default {
  components: {
    TruncateDataWithTooltip,
    draggable
  },
  props: {
    value: Array,
    selectAll: {
      type: Boolean,
      default: false
    },
    boarding: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      selectedDeliveries: {},
      dragOptions: {
        animation: 150,
        group: 'cargosAllocation',
        ghostClass: 'ghost',
        disabled: false,
        handle: '.handle-drag'
      },
      isDragging: false
    };
  },
  computed: {
    files: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit('input', newValue);
      }
    },
    hasDirectDelivery() {
      return this.value.some((cargo) => !cargo.isDirectDelivery);
    },
    truncateLimit() {
      const width = this.$vuetify.breakpoint.width;
      if (width < 1360) return 8;
      if (width < 1440) return 10;
      if (width < 1520) return 16;
      if (width < 1680) return 20;
      if (width < 1820) return 26;
      return 34;
    }
  },
  watch: {
    selectAll() {
      this.toggleSelectAll();
    },
    files: {
      handler() {
        if (!this.files.length) {
          this.$emit('update:selectAll', false);
        }
      }
    },
    selectedDeliveries: {
      handler() {
        this.checkAllSelected();
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('ImportManualBoardingsStore', ['deallocateCargo', 'allocateCargo']),
    async allocateCargoToBoarding(cargos) {
      try {
        let cargosIds;
        if (Array.isArray(cargos)) {
          cargosIds = cargos.map(({ id }) => id);
        } else {
          cargosIds = [cargos.id];
        }
        const params = {
          id: this.boarding.id,
          cargosIds: cargosIds
        };

        const response = await this.allocateCargo(params);
        if (response.success) {
          this.$emit('update:allocated', 'Carga(s) alocadas com sucesso!', true);
        } else {
          const draggedItemIndex = this.files.findIndex((item) => item === cargos);
          if (draggedItemIndex !== -1) {
            this.files.splice(draggedItemIndex, 1);
          }
          this.$emit(
            'update:allocated',
            response.errors[0] || 'Carga(s) com destinatário ou remetente distintos!',
            false
          );
        }
      } catch (err) {
        console.error(err);
      }
    },
    async deallocateCargoFromBoarding(cargos) {
      try {
        let cargosIds;
        if (Array.isArray(cargos)) {
          cargosIds = cargos.map(({ id }) => id);
        } else {
          cargosIds = [cargos.id];
        }
        const params = {
          id: this.boarding.id,
          cargosIds: cargosIds
        };
        const response = await this.deallocateCargo(params);
        if (response.success) {
          this.$emit('update:allocated', 'Carga(s) desalocadas com sucesso!', true);
        } else {
          this.$emit('update:allocated', response.errors[0] || 'Erro ao desalocar Carga(s)', false);
        }
      } catch (err) {
        console.error(err);
      }
    },
    toggleSelectAll() {
      this.selectedDeliveries = {};
      if (this.selectAll) {
        this.files.forEach((delivery) => {
          this.$set(this.selectedDeliveries, delivery.id, true);
        });
      } else {
        this.selectedDeliveries = {};
      }
    },

    onDragStart(e) {
      if (this.files.length > 1) {
        e.item._selected_items = this.files.filter((f) => this.selectedDeliveries[f.id]);
      } else {
        e.item._selected_items = [this.files[0]];
      }
    },
    onDragEnd() {
      this.isDragging = false;
      this.selectedDeliveries = {};
    },
    onUpdate(e) {
      if (e.item._selected_items && e.item._selected_items.length) {
        const ids = e.item._selected_items.map(({ id }) => id);
        const files = this.files.filter((f) => !ids.includes(f.id));
        files.splice(e.newIndex, 0, ...e.item._selected_items);
        this.files = files;
      }
    },
    onRemove(e) {
      let item;
      if (e.item._selected_items && e.item._selected_items.length > 1) {
        item = e.item._selected_items;
      } else if (e.item._underlying_vm_) {
        item = [e.item._underlying_vm_];
      } else {
        console.error('Invalid item on remove:', e.item);
        return;
      }
      this.deallocateCargoFromBoarding(item);
      this.$emit('update:selectAll', false);
    },
    async onAdd(e) {
      this.$emit('loading:allocated', true);

      if (e.item._selected_items.length > 1) {
        const draggedItems = e.item._selected_items;
        await this.allocateCargoToBoarding(draggedItems);
      } else {
        await this.allocateCargoToBoarding(e.item._underlying_vm_);
      }
      this.$emit('loading:allocated', false);
      this.$emit('update:selectAll', false);
    },

    onCheckboxChange() {
      this.checkAllSelected();
    },
    checkAllSelected() {
      const allSelected = this.files.length > 0 && this.files.every((delivery) => this.selectedDeliveries[delivery.id]);
      this.$emit('update:selectAll', allSelected);
    },
    formatPrice(price) {
      return maskPrice(price);
    },
    formatWeight(price) {
      return maskWeight(price);
    },
    truncateText(text) {
      return text && text.length > 20 ? text.substring(0, 18 - 3) + '...' : text;
    },
    copy(str) {
      copyToClipboard(str);
    }
  }
};
</script>

<style scoped>
.ghost {
  display: none;
}
</style>
